import type { SearchTextResultItem } from "@aws-sdk/client-geo-places";
import { AWS_LOCATION_API_KEY, AWS_LOCATION_BASE_URL } from "../constants/api";

export interface PlaceSuggestion {
  placeId: string;
  text: string;
  address: {
    city: string;
    state: string;
    country: string;
  };
  location: {
    lat: number;
    lng: number;
  };
}

// Central North America fallback [longitude, latitude]
const NORTH_AMERICA_BIAS: [number, number] = [-96.0, 54.0];

export async function fetchPlaceSearch(
  input: string,
  biasPosition?: { lat: number; lng: number }
): Promise<PlaceSuggestion[]> {
  const url = `${AWS_LOCATION_BASE_URL}/search-text?key=${encodeURIComponent(AWS_LOCATION_API_KEY)}`;

  const bias: [number, number] = biasPosition ? [biasPosition.lng, biasPosition.lat] : NORTH_AMERICA_BIAS;

  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      QueryText: input,
      BiasPosition: bias, // NA bias. Required to specify
      Filter: {
        IncludePlaceTypes: ["Locality"],
      },
      Language: "en",
      MaxResults: 5,
    }),
  });

  if (!response.ok) {
    throw new Error(`Error: ${response.statusText}`);
  }

  const data = await response.json();

  return (data.ResultItems ?? [])
    .filter((item: SearchTextResultItem) => item.PlaceType !== "PointOfInterest")
    .map((item: SearchTextResultItem) => ({
      placeId: item.PlaceId ?? "",
      text: item.Title ?? "",
      address: {
        city: item.Address?.Locality ?? "",
        state: item.Address?.Region?.Name ?? item.Address?.SubRegion?.Name ?? "",
        country: item.Address?.Country?.Name ?? "",
      },
      location: {
        lat: item.Position?.[1] ?? 0,
        lng: item.Position?.[0] ?? 0,
      },
    }));
}
